import { Injectable, inject } from '@angular/core';
import * as joint from '@clientio/rappid';

import { PortsService } from './ports.service';
import { PositionService } from './position.service';
import { ElementClasses } from '../../model/element-classes.model';
import { FlowchartConfig, FontAttributes } from '../base/flowchart.config';
import { BaseGraph } from '../flowchart/graph/BaseGraph';
import { FlowchartModelService } from '../flowchart/model.service';
import { BasePaper } from '../flowchart/paper/BasePaper';
import { FlowchartViewService } from '../flowchart/view.service';

export type CreateStartNodeOptions = {
  id: string;
  uniqueId: string;
  tooltip: string;
  x: number;
  y: number;
  size: { width: number; height: number };
  colorPresetId: string;
};

@Injectable()
export class StartNodeService {
  private flowchartViewService = inject(FlowchartViewService);
  private flowchartModelService = inject(FlowchartModelService);
  private positionService = inject(PositionService);

  paperSource: BasePaper;
  graphSource: BaseGraph;

  constructor() {
    this.paperSource = this.flowchartViewService.getPaper();
    this.graphSource = this.flowchartModelService.getGraph();
  }

  createStart(text: string, options?: Partial<CreateStartNodeOptions>): joint.dia.Cell {
    const paperSize = this.paperSource.paper.getArea();

    const startElement = new joint.shapes.standard.Rectangle({
      position:
        typeof options?.x === 'number' && typeof options?.y === 'number'
          ? { x: options.x, y: options.y }
          : this.positionService.calculateStartPosition({ width: paperSize.width, height: paperSize.height }),
      size: options?.size ? options?.size : FlowchartConfig.nodeSize.default,
      z: 1,
      attrs: {
        root: {
          'data-tooltip': options?.tooltip,
          'data-flowchart-element-type': `start`,
        },
        body: {
          class: `element--start__body ${ElementClasses.ElementBody}`,
          rx: FlowchartConfig.startNode.styles.borderRadius,
          ry: FlowchartConfig.startNode.styles.borderRadius,
          uniqueId: options?.uniqueId ? options?.uniqueId : '',
          fill: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-fill) !important` : undefined,
          stroke: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-stroke) !important` : undefined,
          'data-testid': `flowchart-element_body`,
        },
        label: {
          class: `element--start__text ${ElementClasses.ElementText}`,
          'data-testid': `flowchart-element_label`,
          fill: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-text) !important` : undefined,
          ...FontAttributes,
          fontSize: FlowchartConfig.startNode.styles.fontSize,
          text,
          textWrap: {
            width: -10,
            ellipsis: true,
          },
          textVerticalAnchor: 'middle',
        },
      },
    });

    startElement.set('id', options?.id ?? startElement.id);
    startElement.attr('body/unique-id', options?.uniqueId ?? startElement.id);

    startElement.set('startNode', true);

    PortsService.addPorts(startElement);

    return startElement;
  }
}
